import React from 'react';
import { Button } from '../Button';
import { cn } from '../../utils/cn';

export interface FloatingActionButtonProps {
  /**
   * Icon to display in the FAB
   */
  icon: React.ReactNode;

  /**
   * Click handler
   */
  onClick?: () => void;

  /**
   * Position of the FAB
   */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

  /**
   * Size of the FAB
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Variant/color of the FAB
   */
  variant?: 'primary' | 'secondary' | 'accent' | 'neutral';

  /**
   * Accessibility label
   */
  ariaLabel: string;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Whether the FAB is visible
   */
  visible?: boolean;

  /**
   * z-index for layering
   */
  zIndex?: number;
}

const positionClasses = {
  'bottom-right': 'bottom-6 right-6',
  'bottom-left': 'bottom-6 left-6',
  'top-right': 'top-6 right-6',
  'top-left': 'top-6 left-6',
};

const sizeClasses = {
  sm: 'w-12 h-12',
  md: 'w-14 h-14',
  lg: 'w-16 h-16',
};

export const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  icon,
  onClick,
  position = 'bottom-right',
  size = 'md',
  variant = 'primary',
  ariaLabel,
  className,
  visible = true,
  zIndex = 50,
}) => {
  if (!visible) return null;

  return (
    <Button
      variant={variant}
      size={size}
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        'fixed shadow-lg hover:shadow-xl transition-all duration-200',
        'rounded-full border-0',
        sizeClasses[size],
        'flex items-center justify-center',
        positionClasses[position],
        className
      )}
      style={{ zIndex }}
    >
      {icon}
    </Button>
  );
};
